import { Fragment, Component } from "react"
import { bulkUpdateSourceDataFieldByGroupKeyInfo } from "../../../actions/bulk-actions"
import { HoursSegment, TotalColumnHoursSegment } from "./cell-renderer-util"
import { isPivotRowTotalColumn, roleHasWritePermission, getValuePivotCellClass, getTotalPivotCellClass } from "../../../common/ag-grid-utils"
import { getGroupKeyInfo } from "../../../common/ag-grid-grouping-utils"
import {
    colorAttentionRed50,
    colorAttentionYellow50,
    colorFunctionalBlack,
    colorFunctionalGray30,
    colorFunctionalGray70,
    colorFunctionalWhite,
    IconAbsent,
    IconAddModifier,
    IconException,
    IconPalmTree,
    IconPalmTreeFilled,
    IconShiftExtra,
    IconSignature,
    IconTimeCard,
} from "@rhumbix/rmbx_design_system_web"
import { statusesByUserRole } from "../../../common/constants"
import { getFlagEnabled } from "../../../getFlagValue"
import { connect } from "react-redux"
import ReactDOM from "react-dom"
import { computeCellPTOStatus, selectPTOFulfillment, selectPTORecordsByEmployeeDate } from "../../../selectors/pto"
import { Tooltip as ReactTooltip } from "react-tooltip"
import Portal from "react-overlays/Portal"
import { pivotTotalColumnCustomTooltipMessageBuilder, pivotValueColumnCustomTooltipMessageBuilder } from "../../../common/ag-grid-custom-tooltip-formatters"
import { formatTimeDiff, getCicoIcon, getCicoIconColor, getSignatureIconColor, getSignatureIconState } from "../utils"
import { roundDurationToNearestIncrement } from "../../../common/ts-utils"
import { getCellSourceData } from "../../../common/ag-grid-ts-utils"

const CICO_TOOLTIP_ID = "cico-tooltip"
const EXCEPTIONS_TOOLTIP_ID = "exceptions-tooltip"
const PTO_TOOLTIP_ID = "pto-tooltip"
const NON_NUMERIC_FIELDS = ["/cost_code", "/status", "/work_shift_id"]

// All values needed to render the tooltip body are read from the active anchor's data-attributes,
// so the function can live at module scope and never closes over per-row state.
const renderCicoTooltipBody = attrs => {
    const { activeAnchor } = attrs
    const fieldInner = activeAnchor?.getAttribute("data-field")
    const editCountInner = activeAnchor?.getAttribute("data-edit-count")
    const workingDurationInner = activeAnchor?.getAttribute("data-clocked-time")
    const shiftCountInner = activeAnchor?.getAttribute("data-num-shifts")
    const timeDiffInner = activeAnchor?.getAttribute("data-time-diff")
    const allTimeDiffInner = activeAnchor?.getAttribute("data-all-time-diff")
    const allTimeInner = parseFloat(activeAnchor?.getAttribute("data-all-time")) || 0
    const timeDiffStrInner = NON_NUMERIC_FIELDS.includes(fieldInner) || !timeDiffInner ? "" : `, ${formatTimeDiff(parseFloat(timeDiffInner))}`
    const allTimeDiffStrInner = NON_NUMERIC_FIELDS.includes(fieldInner) || !parseFloat(allTimeDiffInner) ? "" : `, ${formatTimeDiff(parseFloat(allTimeDiffInner))} non-CI/CO Hours`
    const shiftCountStr = ("0" + shiftCountInner).slice(-2)
    const shiftStr = getFlagEnabled("WA-8985-work-rules") ? `(${shiftCountStr} ${shiftCountInner > 1 ? "Shifts" : "Shift"}${allTimeDiffStrInner})` : ""

    const tooltipLineInner = workingDurationInner ? `${parseFloat(workingDurationInner).toFixed(2)} CI/CO Hours${timeDiffStrInner} ${shiftStr}` : ""

    return (
        <span style={{ textAlign: "left", display: "block" }}>
            <span data-testId={"cico-tooltip"} style={{ fontWeight: "bold" }}>
                {tooltipLineInner}
            </span>
            <br />
            {!getFlagEnabled("WA-8985-work-rules") && (
                <>
                    {("0" + shiftCountInner).slice(-2)} Shifts
                    <br />
                </>
            )}
            {editCountInner > 0 ? (
                <>
                    <span data-testId={"cico-tooltip"} style={{ fontWeight: "bold" }}>
                        {`${editCountInner} Edits`}
                    </span>
                    <br />
                </>
            ) : (
                ""
            )}
            {getFlagEnabled("WA-8985-work-rules") && (
                <>
                    <span data-testId={"cico-tooltip"} style={{ fontWeight: "bold" }}>
                        Daily Hours: {allTimeInner.toFixed(2)}
                    </span>
                    <br />
                </>
            )}
        </span>
    )
}

const renderExceptionsTooltipBody = attrs => {
    const { activeAnchor } = attrs
    const counts = activeAnchor?.getAttribute("data-violations") ? JSON.parse(activeAnchor?.getAttribute("data-violations")) : {}
    const items = {}
    let totalCount = 0
    for (const [setName, rules] of Object.entries(counts)) {
        items[setName] = []
        for (const [ruleName, { warnings = 0, errors = 0 }] of Object.entries(rules)) {
            const isError = errors > 0
            items[setName].push({
                key: `${setName}-${ruleName}`,
                ruleName,
                setName,
                count: isError ? errors : warnings,
                isError,
            })
            totalCount += isError ? errors : warnings
        }
        items[setName].sort((a, b) => (b.isError ? 1 : 0) - (a.isError ? 1 : 0))
    }

    return (
        <span style={{ textAlign: "left", display: "block", color: colorFunctionalGray30 }}>
            <div>
                <div>
                    <span
                        style={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            padding: "0 6px",
                            borderRadius: 8,
                            fontWeight: "bold",
                            backgroundColor: colorAttentionRed50,
                            color: colorFunctionalWhite,
                        }}
                    >
                        !
                    </span>
                    <b>&nbsp;&nbsp;Work Rule Exceptions ({totalCount})</b>
                </div>
                <hr style={{ margin: "4px 0", borderColor: colorFunctionalGray70 }} />
                {Object.entries(items).map(([setName, violations]) => {
                    return (
                        <div key={setName}>
                            <b>{setName}</b>
                            {violations.map(({ key, ruleName, count, isError }) => (
                                <div
                                    key={key}
                                    style={{
                                        borderLeft: `3px solid ${isError ? colorAttentionRed50 : colorAttentionYellow50}`,
                                        padding: "4px 8px",
                                        marginBottom: "4px",
                                        marginLeft: "16px",
                                        justifyContent: "space-between",
                                        display: "flex",
                                    }}
                                >
                                    <span>{ruleName} &mdash; </span>
                                    <span>{count}</span>
                                </div>
                            ))}
                        </div>
                    )
                })}
            </div>
        </span>
    )
}

const renderPTOTooltipBody = attrs => {
    const { activeAnchor } = attrs
    const names = activeAnchor?.getAttribute("data-pto-names") ? JSON.parse(activeAnchor.getAttribute("data-pto-names")) : []

    return (
        <div style={{ textAlign: "left" }}>
            {names.map(name => (
                <div key={name}>{name}</div>
            ))}
        </div>
    )
}

// Mount the shared CI/CO + Exceptions tooltips into the page-level ``master-container`` exactly once,
// via a dedicated React root. The cell renderer mounts in many rows; using a single shared tooltip per
// type prevents duplicate ReactTooltip instances from coexisting in the DOM (the prior per-row Portal
// pattern stacked many tooltips into the same container and let the render function close over the
// originating row's state).
//
// We track the host element rather than a boolean flag so that SPA navigation away from a page
// containing ``master-container`` (which detaches our host along with it) doesn't leave the module
// permanently stuck in a "mounted" state — when the user returns to Weekly View, the detached host's
// ``isConnected`` will be ``false`` and we re-mount into the freshly created ``master-container``.
let sharedTooltipsHost = null
const ensureSharedTooltipsMounted = () => {
    if (sharedTooltipsHost && sharedTooltipsHost.isConnected) return
    if (sharedTooltipsHost) {
        ReactDOM.unmountComponentAtNode(sharedTooltipsHost)
        sharedTooltipsHost = null
    }
    const container = document.getElementById("master-container")
    if (!container) return
    const host = document.createElement("div")
    container.appendChild(host)
    ReactDOM.render(
        <Fragment>
            <ReactTooltip id={CICO_TOOLTIP_ID} place="bottom" variant="dark" render={renderCicoTooltipBody} />
            <ReactTooltip id={EXCEPTIONS_TOOLTIP_ID} place="bottom" variant="dark" render={renderExceptionsTooltipBody} />
            <ReactTooltip id={PTO_TOOLTIP_ID} place="bottom" variant="dark" render={renderPTOTooltipBody} />
        </Fragment>,
        host
    )
    sharedTooltipsHost = host
}

class TimecardCellRenderer extends Component {
    constructor(props) {
        super(props)
        this.updateStatus = this.updateStatus.bind(this)
    }

    componentDidMount() {
        if (getFlagEnabled("WA-9439-weekly-view-tooltip-fix")) {
            ensureSharedTooltipsMounted()
        }
    }

    getFontColor = color => {
        if (!color) return "#fff"
        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(color)
        const rgbColor = result
            ? {
                  r: parseInt(result[1], 16),
                  g: parseInt(result[2], 16),
                  b: parseInt(result[3], 16),
              }
            : null

        const uicolors = [rgbColor.r / 255, rgbColor.g / 255, rgbColor.b / 255]
        const c = uicolors.map(col => {
            if (col <= 0.03928) {
                return col / 12.92
            }
            return Math.pow((col + 0.055) / 1.055, 2.4)
        })
        const L = 0.2126 * c[0] + 0.7152 * c[1] + 0.0722 * c[2]
        return L > 0.179 ? colorFunctionalBlack : colorFunctionalWhite
    }

    updateStatus(event) {
        event.stopPropagation()
        const status = event.target.value
        const pivotColumns = this.props.columnApi.getPivotColumns()
        const groupKeyInfo = getGroupKeyInfo(this.props.node, this.props.column, pivotColumns, this.props.context.referenceableData)
        if (getFlagEnabled("WA-8985-work-rules")) {
            this.props.context.dispatch(
                bulkUpdateSourceDataFieldByGroupKeyInfo(groupKeyInfo, "status", status, this.props.context, {
                    rowsWithWorkRuleErrors: this.rowsWithWorkRuleErrorIds(),
                })
            )
        } else {
            this.props.context.dispatch(bulkUpdateSourceDataFieldByGroupKeyInfo(groupKeyInfo, "status", status, this.props.context))
        }
    }

    rowsWithWorkRuleErrorIds() {
        // The status dropdown applies to every leaf row under this row node — including
        // total cells that have no pivot column to anchor on — so we walk all leaf children
        // directly rather than going through ``getCellSourceData`` (which filters by
        // ``column.pivotKeys[0]`` and would return nothing for total cells).
        // ``workRuleErrorMessages`` is only populated when the work rules annotator has run
        // (see ``selectors/custom-dashboard.js``), so this returns an empty set in non-Weekly
        // contexts. The set is keyed by ``id ?? gridId`` so the action can match against raw
        // source-data rows when partitioning out the rows it shouldn't update.
        const leafChildren = this.props.node?.allLeafChildren || []
        const ids = new Set()
        leafChildren.forEach(child => {
            const row = child?.data
            if (Array.isArray(row?.workRuleErrorMessages) && row.workRuleErrorMessages.length) {
                const key = row.id ?? row.gridId
                if (key !== undefined && key !== null) ids.add(key)
            }
        })
        return ids
    }

    renderIcons() {
        const useSharedTooltips = getFlagEnabled("WA-9439-weekly-view-tooltip-fix")
        const cicoIconSpot = useSharedTooltips ? null : document.getElementById("master-container")
        let totalTime = this.props.value.cicoTime || 0
        const timekeepingIncrement = this.props.context.currentUser.company_options.timekeeping_increment ?? 15
        // Take each project's duration and round it to the nearest timekeeping increment
        // (using the project's rounding increment) then sum the durations to get the total working duration
        let workingDuration = 0
        let allTime = 0
        if (!getFlagEnabled("WA-8985-work-rules")) {
            workingDuration = getFlagEnabled("RN-2134-round-cico-totals")
                ? Object.entries(this.props.value.cicoByProject || {}).reduce((acc, [projectId, duration]) => {
                      const project = this.props.context.referenceableData.projects[projectId]
                      const roundingIncrement = project.cico_settings?.rounding_increment
                      const roundedDuration = roundDurationToNearestIncrement(duration, timekeepingIncrement, roundingIncrement)
                      return acc + roundedDuration
                  }, 0) / 60
                : (this.props.value.clocked_in_duration || 0) / 60 - (this.props.value.meal_duration || 0) / 60
        }

        const value = this.props.value
        // violationCounts: { setName: { ruleName: { warnings: N, errors: N } } }
        // Deduplicates by rule + message so shift-level rules only count once per unique message
        const violationCounts = {}
        const seenViolations = new Set()
        const addViolation = (msg, tkEntry, severity) => {
            const setName = typeof msg === "object" ? msg.workRuleSetName || "" : ""
            const ruleName = typeof msg === "object" ? msg.ruleName || "" : msg || ""
            const dedupKey = `${setName}|${ruleName}|${msg.message}`
            if (seenViolations.has(dedupKey)) return
            seenViolations.add(dedupKey)
            if (!violationCounts[setName]) violationCounts[setName] = {}
            if (!violationCounts[setName][ruleName]) {
                violationCounts[setName][ruleName] = { warnings: 0, errors: 0 }
            }
            violationCounts[setName][ruleName][severity] += 1
        }

        if (
            getFlagEnabled("WA-8985-work-rules") &&
            this.props.exceptions.tkSummary &&
            ["/employee", "/project", "/foreman"].includes(this.props.node.field) &&
            typeof value === "object" &&
            value &&
            Object.keys(value).length > 0
        ) {
            const cellSourceData = getCellSourceData(this.props.node, this.props.column)
            let cellDate
            const employees = new Set()
            Object.values(cellSourceData).forEach(tkEntries => {
                if (!tkEntries) return
                tkEntries.forEach(tkEntry => {
                    if (!tkEntry.id) return
                    const employee = tkEntry.employee || tkEntry.worker_id
                    cellDate = tkEntry.date
                    employees.add(employee)
                })
            })

            workingDuration = 0
            totalTime = 0
            allTime = 0
            for (const employee of Array.from(employees)) {
                // tkSummary may contain multiple rows per (employee, shift_date) when grouped
                // by cost_code_id. ``st/ot/dt`` are per-cost-code so they sum, but
                // ``rounded_cico_time`` and ``cico_project_tk_minutes`` are per-day values
                // duplicated onto every row — count them once per shift_date.
                const rowsForDate = this.props.exceptions.tkSummary[employee]?.filter(summary => summary.shift_date === cellDate)

                if (!rowsForDate?.length) continue
                const employeeSummary = rowsForDate.reduce(
                    (acc, entry) => {
                        acc.st += entry.st
                        acc.ot += entry.ot
                        acc.dt += entry.dt
                        return acc
                    },
                    {
                        rounded_cico_time: rowsForDate[0].rounded_cico_time || 0,
                        cico_project_tk_minutes: rowsForDate[0].cico_project_tk_minutes || 0,
                        st: 0,
                        dt: 0,
                        ot: 0,
                    }
                )
                workingDuration += employeeSummary.rounded_cico_time / 60
                totalTime += employeeSummary.cico_project_tk_minutes / 60
                allTime += (employeeSummary.st + employeeSummary.ot + employeeSummary.dt) / 60
            }

            if (this.props.exceptions.workRulesException.enabled) {
                Object.values(cellSourceData).forEach(tkEntries => {
                    if (!tkEntries) return
                    tkEntries.forEach(tkEntry => {
                        const warnings = tkEntry?.workRuleWarningMessages?.length ? tkEntry.workRuleWarningMessages : Array.isArray(tkEntry?.workRuleWarnings) ? tkEntry.workRuleWarnings : []
                        const errs = tkEntry?.workRuleErrorMessages?.length ? tkEntry.workRuleErrorMessages : Array.isArray(tkEntry?.workRuleErrors) ? tkEntry.workRuleErrors : []
                        warnings.forEach(w => addViolation(w, tkEntry, "warnings"))
                        errs.forEach(e => addViolation(e, tkEntry, "errors"))
                    })
                })
            }
        }
        const hasExceptions = Object.keys(violationCounts).length > 0

        const timeDiff = ["/cost_code", "/status", "/work_shift_id"].includes(this.props.node.field) ? 0 : totalTime - workingDuration
        const allTimeDiff = allTime - totalTime
        const CicoIcon = getCicoIcon(timeDiff)
        const cicoIconColor = getCicoIconColor(timeDiff, totalTime)
        const signatureIconState = getFlagEnabled("WA-9467-signature-revalidation") && this.props.value.signatureState?.size ? getSignatureIconState(this.props.value.signatureState) : null

        let ptoRequested = false
        let ptoFulfilled = false
        let ptoNames = []
        if (getFlagEnabled("WA-9197-pto-accruals")) {
            let employeeNode = this.props.node
            while (employeeNode && employeeNode.field !== "/employee") employeeNode = employeeNode.parent
            const employeeId = employeeNode ? Number(employeeNode.key) : null
            const date = this.props.column?.getColDef()?.pivotKeys?.[0]
            const costCodeId = this.props.node.field === "/cost_code" ? Number(this.props.node.key) : null
            ;({ ptoRequested, ptoFulfilled, ptoNames } = computeCellPTOStatus({
                employeeId,
                date,
                costCodeId,
                recordsByEmployeeDate: this.props.ptoRecordsByEmployeeDate,
                ptoFulfillment: this.props.ptoFulfillment,
                costCodes: this.props.context.referenceableData.costCodes,
            }))
        }
        const palmTreeTooltipProps = useSharedTooltips
            ? {
                  "data-tooltip-id": PTO_TOOLTIP_ID,
                  "data-pto-names": JSON.stringify(ptoNames),
              }
            : {}

        return (
            <div className="icon-row">
                {getFlagEnabled("WA-9197-pto-accruals") ? (
                    <div className="icon-item">{ptoRequested ? ptoFulfilled ? <IconPalmTreeFilled {...palmTreeTooltipProps} /> : <IconPalmTree {...palmTreeTooltipProps} /> : null}</div>
                ) : null}
                <div className={"icon-item"}>
                    {"clocked_in_duration" in this.props.value ? (
                        <div id={"cico-icon"}>
                            {!useSharedTooltips && (
                                <Portal container={cicoIconSpot}>
                                    <ReactTooltip
                                        id={`cico-tooltip-${this.props.node.id}`}
                                        place="bottom"
                                        variant="dark"
                                        render={attrs => {
                                            const { activeAnchor } = attrs
                                            const fieldInner = activeAnchor?.getAttribute("data-field")
                                            const editCountInner = activeAnchor?.getAttribute("data-edit-count")
                                            const workingDurationInner = activeAnchor?.getAttribute("data-clocked-time")
                                            const shiftCountInner = activeAnchor?.getAttribute("data-num-shifts")
                                            const timeDiffInner = activeAnchor?.getAttribute("data-time-diff")
                                            const allTimeDiffInner = activeAnchor?.getAttribute("data-all-time-diff")
                                            const timeDiffStrInner =
                                                ["/cost_code", "/status", "/work_shift_id"].includes(fieldInner) || !timeDiffInner ? "" : `, ${formatTimeDiff(parseFloat(timeDiffInner))}`
                                            const allTimeDiffStrInner =
                                                ["/cost_code", "/status", "/work_shift_id"].includes(fieldInner) || !parseFloat(allTimeDiffInner)
                                                    ? ""
                                                    : `, ${formatTimeDiff(parseFloat(allTimeDiffInner))} non-CI/CO Hours`
                                            const shiftCountStr = ("0" + shiftCountInner).slice(-2)
                                            const shiftStr = getFlagEnabled("WA-8985-work-rules") ? `(${shiftCountStr} ${shiftCountInner > 1 ? "Shifts" : "Shift"}${allTimeDiffStrInner})` : ""

                                            const tooltipLineInner = workingDurationInner ? `${parseFloat(workingDurationInner).toFixed(2)} CI/CO Hours${timeDiffStrInner} ${shiftStr}` : ""

                                            return (
                                                <span style={{ textAlign: "left", display: "block" }}>
                                                    <span data-testId={"cico-tooltip"} style={{ fontWeight: "bold" }}>
                                                        {tooltipLineInner}
                                                    </span>
                                                    <br />
                                                    {!getFlagEnabled("WA-8985-work-rules") && (
                                                        <>
                                                            {("0" + activeAnchor?.getAttribute("data-num-shifts")).slice(-2)} Shifts
                                                            <br />
                                                        </>
                                                    )}
                                                    {editCountInner > 0 ? (
                                                        <>
                                                            <span data-testId={"cico-tooltip"} style={{ fontWeight: "bold" }}>
                                                                {`${editCountInner} Edits`}
                                                            </span>
                                                            <br />
                                                        </>
                                                    ) : (
                                                        ""
                                                    )}
                                                    {getFlagEnabled("WA-8985-work-rules") && (
                                                        <>
                                                            <span data-testId={"cico-tooltip"} style={{ fontWeight: "bold" }}>
                                                                Daily Hours: {allTime.toFixed(2)}
                                                            </span>
                                                            <br />
                                                        </>
                                                    )}
                                                </span>
                                            )
                                        }}
                                    />
                                </Portal>
                            )}
                            <CicoIcon
                                alt={"clock-in/clock-out icon"}
                                data-clocked-time={workingDuration}
                                data-time-diff={timeDiff}
                                data-all-time-diff={allTimeDiff}
                                data-all-time={allTime}
                                data-tooltip-id={useSharedTooltips ? CICO_TOOLTIP_ID : `cico-tooltip-${this.props.node.id}`}
                                data-field={this.props.node.field}
                                data-num-shifts={this.props.value.shift_count || 0}
                                data-edit-count={this.props.value.version_count - this.props.value.item_count}
                                data-tooltip-position-strategy={"fixed"}
                                style={{ color: cicoIconColor }}
                            />
                        </div>
                    ) : null}
                </div>
                <div className="icon-item">
                    {"absenceReason" in this.props.value ? (
                        <div id="absence-icon">
                            <IconAbsent title="Absence" />
                        </div>
                    ) : null}
                </div>
                <div className="icon-item">
                    {"shiftExtra" in this.props.value ? (
                        <div id="shift-extra-icon">
                            <IconShiftExtra title="Shift Extra" />
                        </div>
                    ) : null}
                </div>
                <div className="icon-item">
                    {getFlagEnabled("WA-9467-signature-revalidation") ? (
                        signatureIconState ? (
                            <div id="signature-icon" data-signature-state={signatureIconState}>
                                <IconSignature title="Signature" style={{ color: getSignatureIconColor(this.props.value.signatureState) }} />
                            </div>
                        ) : null
                    ) : "signature" in this.props.value && this.props.value["signature"] ? (
                        <div id="signature-icon">
                            <IconSignature title="Signature" />
                        </div>
                    ) : null}
                </div>
                <div className="icon-item">{this.props.value?.modifierActive ? <IconAddModifier className="modifier-badge" title="Modifier" /> : null}</div>
                <div className="icon-item">
                    {"startStopTypes" in this.props.value && this.props.value["startStopTypes"]?.size ? (
                        <div id="start-stop-icon">
                            <IconTimeCard title="Shift Start/Stop Times" />
                        </div>
                    ) : null}
                </div>
                {hasExceptions ? (
                    <div className="icon-item">
                        {!useSharedTooltips && (
                            <Portal container={cicoIconSpot}>
                                <ReactTooltip
                                    id={`exceptions-tooltip-${this.props.node.id}`}
                                    place="bottom"
                                    variant="dark"
                                    render={attrs => {
                                        const { activeAnchor } = attrs
                                        const counts = activeAnchor?.getAttribute("data-violations") ? JSON.parse(activeAnchor?.getAttribute("data-violations")) : {}
                                        const items = {}
                                        let totalCount = 0
                                        for (const [setName, rules] of Object.entries(counts)) {
                                            items[setName] = []
                                            for (const [ruleName, { warnings = 0, errors = 0 }] of Object.entries(rules)) {
                                                const isError = errors > 0
                                                items[setName].push({
                                                    key: `${setName}-${ruleName}`,
                                                    ruleName,
                                                    setName,
                                                    count: isError ? errors : warnings,
                                                    isError,
                                                })
                                                totalCount += isError ? errors : warnings
                                            }
                                            items[setName].sort((a, b) => (b.isError ? 1 : 0) - (a.isError ? 1 : 0))
                                        }

                                        return (
                                            <span
                                                style={{
                                                    textAlign: "left",
                                                    display: "block",
                                                    color: colorFunctionalGray30,
                                                }}
                                            >
                                                <div>
                                                    <div>
                                                        <span
                                                            style={{
                                                                display: "inline-flex",
                                                                alignItems: "center",
                                                                justifyContent: "center",
                                                                padding: "0 6px",
                                                                borderRadius: 8,
                                                                fontWeight: "bold",
                                                                backgroundColor: colorAttentionRed50,
                                                                color: colorFunctionalWhite,
                                                            }}
                                                        >
                                                            !
                                                        </span>
                                                        <b>&nbsp;&nbsp;Work Rule Exceptions ({totalCount})</b>
                                                    </div>
                                                    <hr
                                                        style={{
                                                            margin: "4px 0",
                                                            borderColor: colorFunctionalGray70,
                                                        }}
                                                    />
                                                    {Object.entries(items).map(([setName, violations]) => {
                                                        return (
                                                            <div key={setName}>
                                                                <b>{setName}</b>
                                                                {violations.map(({ key, ruleName, count, isError }) => (
                                                                    <div
                                                                        key={key}
                                                                        style={{
                                                                            borderLeft: `3px solid ${isError ? colorAttentionRed50 : colorAttentionYellow50}`,
                                                                            padding: "4px 8px",
                                                                            marginBottom: "4px",
                                                                            marginLeft: "16px",
                                                                            justifyContent: "space-between",
                                                                            display: "flex",
                                                                        }}
                                                                    >
                                                                        <span>{ruleName} &mdash; </span>
                                                                        <span>{count}</span>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )
                                                    })}
                                                </div>
                                            </span>
                                        )
                                    }}
                                />
                            </Portal>
                        )}
                        <IconException
                            title="Exceptions"
                            data-tooltip-id={useSharedTooltips ? EXCEPTIONS_TOOLTIP_ID : `exceptions-tooltip-${this.props.node.id}`}
                            data-violations={JSON.stringify(violationCounts)}
                        />
                    </div>
                ) : null}
            </div>
        )
    }

    createStyleForStatus = status => ({
        backgroundColor: status?.is_outline ? "" : `#${status ? status.selected_color : "CCCCCC"}`,
        borderWidth: status?.is_outline ? "2px" : "1px",
        borderColor: status?.is_outline ? `#${status.selected_color}` : colorFunctionalBlack,
        fontWeight: 500,
        color: status?.is_outline ? `#${status?.selected_color}` : this.getFontColor(status?.selected_color),
    })
    renderApproveButton() {
        const { user_role } = this.props.context.currentUser
        if (!this.props.value.status || this.props.node.field === "cost_code") {
            return null
        }
        const statusValueArray = Array.from(this.props.value.status).filter(status => status !== "__NONE__")
        const editableStatuses = this.props.timekeepingStatuses ? Object.values(this.props.timekeepingStatuses.objects).filter(tk_status => tk_status.can_edit_cell?.includes(user_role)) : []

        const lacksEditPermission = statusValueArray.every(status => !editableStatuses.find(tk_status => tk_status.name === status))
        const buttonDisabled = lacksEditPermission
        const disabledTooltip = lacksEditPermission ? "You don't have permission to change this status." : undefined

        // if the button is disable we need to show the status the user doesn't have access to otherwise
        // it'll appear they can't transition a status they do have access to. Since it's disabled we'll
        // just populate all statuses. In the enabled state we'll only give them statuses they can work with.
        const transitionableStatuses = buttonDisabled
            ? Object.values(this.props.timekeepingStatuses.objects)
            : this.props.timekeepingStatuses
            ? Object.values(this.props.timekeepingStatuses.objects).filter(tk_status => tk_status.can_change_to_status?.includes(user_role) && !tk_status.is_hidden)
            : []
        // SYNCED should always exist, but it might be possible that the timekeeping status referenceable
        // objects aren't loaded yet, so we need to check for that. We make sure to check that this
        // is defined below before trying to access it
        const syncedStatus = this.props.timekeepingStatuses ? Object.values(this.props.timekeepingStatuses.objects).find(s => s.name === "SYNCED") : null

        let css = null
        let style = this.createStyleForStatus(syncedStatus)
        let onlySyncedStatus = false

        css = "select-dropdown"
        // Pick up the styles for the first status in the set of statuses
        if (statusValueArray.length === 1) {
            if ("SYNCED" in statusValueArray && syncedStatus) onlySyncedStatus = true
            Object.values(this.props.timekeepingStatuses?.objects || {})
                .sort((a, b) => (a.sort_order < b.sort_order ? 1 : -1))
                .forEach(tkStatus => {
                    if (statusValueArray.includes(tkStatus.name)) {
                        style = this.createStyleForStatus(tkStatus)
                    }
                })
        } else if (statusValueArray.length > 1) {
            style = this.createStyleForStatus({ is_outline: false, selected_color: "CCCCCC" })
        }

        const firstStatusOption = statusValueArray[0]
        const multipleStatuses = statusValueArray.length > 1 ? ["Multiple Statuses"] : []
        const tooltipId = `disabled-status-tooltip-${this.props.node?.id ?? "row"}`
        const showDisabledTooltip = buttonDisabled && Boolean(disabledTooltip)
        const tooltipAnchorAttrs = showDisabledTooltip ? { "data-tooltip-id": tooltipId, "data-tooltip-content": disabledTooltip } : {}
        if (getFlagEnabled("WA-8101-bulk-status-update-fix")) {
            if (statusValueArray.length === 0) {
                return
            }
            return (
                <div className={css} style={{ ...style, backgroundColor: "" }} {...tooltipAnchorAttrs}>
                    <Fragment>
                        <select
                            id="total-cell-status-select"
                            onChange={this.updateStatus}
                            value={multipleStatuses.length ? multipleStatuses[0] : firstStatusOption}
                            disabled={buttonDisabled}
                            style={style}
                        >
                            {[
                                ...multipleStatuses.map(s => (
                                    <option value={s} key={-1}>
                                        {s}
                                    </option>
                                )),
                                ...(onlySyncedStatus
                                    ? [
                                          <option value={syncedStatus.name} key={0}>
                                              {syncedStatus.label}
                                          </option>,
                                      ]
                                    : transitionableStatuses
                                          .sort((a, b) => (a.sort_order < b.sort_order ? -1 : 1))
                                          .map((item, index) => {
                                              return (
                                                  <option value={item.name} key={index}>
                                                      {item.label}
                                                  </option>
                                              )
                                          })),
                            ]}
                        </select>
                        <div
                            className="timecard-select__arrow"
                            style={{
                                borderColor: style.color ? `${style.color} transparent transparent transparent` : "",
                            }}
                        />
                        {showDisabledTooltip && (
                            <Portal container={document.getElementById("master-container")}>
                                <ReactTooltip id={tooltipId} place="bottom" variant="dark" />
                            </Portal>
                        )}
                    </Fragment>
                </div>
            )
        }
        return (
            <div className={css} {...tooltipAnchorAttrs}>
                {this.props.value.status.size > 1 ? (
                    <div className="multiple-statuses">Multiple Statuses</div>
                ) : firstStatusOption === "SYNCED" ? (
                    <div className="synced-status-select">Synced</div>
                ) : (
                    <Fragment>
                        <select id="total-cell-status-select" onChange={this.updateStatus} value={firstStatusOption} disabled={!roleHasWritePermission(firstStatusOption, user_role)}>
                            {statusesByUserRole.map((item, index) => {
                                if (this.props.value.status.has(item.name) || item.canChangeToStatus.includes(user_role)) {
                                    return (
                                        <option value={item.name} key={index}>
                                            {item.label}
                                        </option>
                                    )
                                }
                            })}
                        </select>
                        <div className="timecard-select__arrow" />
                        {showDisabledTooltip && (
                            <Portal container={document.getElementById("master-container")}>
                                <ReactTooltip id={tooltipId} place="bottom" variant="dark" />
                            </Portal>
                        )}
                    </Fragment>
                )}
            </div>
        )
    }

    render() {
        const tooltipInfo = pivotValueColumnCustomTooltipMessageBuilder({
            colDef: this.props.colDef,
            column: this.props.column,
            columnApi: this.props.columnApi,
            context: this.props.context,
            node: this.props.node,
            value: this.props.value,
        })

        const tooltipArray = Array.isArray(tooltipInfo.data?.messages) ? tooltipInfo.data?.messages : tooltipInfo.data?.messages ? [tooltipInfo.data.messages] : []

        if (isPivotRowTotalColumn(this.props.colDef)) {
            const totalTooltipInfo = pivotTotalColumnCustomTooltipMessageBuilder({
                colDef: this.props.colDef,
                column: this.props.column,
                columnApi: this.props.columnApi,
                context: this.props.context,
                node: this.props.node,
                value: this.props.value,
            })

            const cellClass = getTotalPivotCellClass(this.props)
            return (
                <div className={`week-total ${cellClass}`}>
                    <TotalColumnHoursSegment value={this.props.value} tooltip={totalTooltipInfo.data?.messages} />
                    {this.renderApproveButton()}
                </div>
            )
        } else {
            const className = this.props.inline ? "one-timekeeping-value-row" : "two-timekeeping-value-rows"
            const cellClass = getValuePivotCellClass(this.props)
            return (
                <div id={this.props.colDef.headerName} className={"weekly-tk-timecard-cell"}>
                    <div className={`${className} ${cellClass}`}>
                        <HoursSegment value={this.props.value} tooltips={tooltipArray} nodeId={this.props.node.id} />
                        {this.renderIcons()}
                    </div>
                    {this.props.value.status && this.props.value.status.size > 0 ? (
                        <div style={{ display: "flex", flexDirection: "column" }}>
                            <div className="traffic-lights">
                                {Object.values(this.props.timekeepingStatuses.objects)
                                    .sort((a, b) => (a.sort_order < b.sort_order ? -1 : 1))
                                    .map((value, index) => {
                                        if (index < 4) {
                                            const enableTrafficLight = this.props.value.status.has(value.name) || (index === 3 && this.props.value.status.has("SYNCED"))
                                            // If we're in the fourth traffic light and we have a synced status,
                                            // we use that bubble to display synced.
                                            const status =
                                                index === 3 && this.props.value.status.has("SYNCED")
                                                    ? getFlagEnabled("WA-8969-synced-status-bubble")
                                                        ? this.props.timekeepingStatuses.objects.SYNCED
                                                        : {
                                                              selected_color: "CCCCCC",
                                                              traffic_color: "606060",
                                                              is_outline: false,
                                                              is_traffic_outline: false,
                                                          }
                                                    : value
                                            return (
                                                <div
                                                    key={index}
                                                    style={
                                                        enableTrafficLight
                                                            ? {
                                                                  backgroundColor: status.is_traffic_outline ? colorFunctionalWhite : `#${status.traffic_color}`,
                                                                  borderWidth: status.is_traffic_outline ? "2px" : "1px",
                                                                  borderColor: status.is_traffic_outline ? `#${status.traffic_color}` : colorFunctionalBlack,
                                                              }
                                                            : null
                                                    }
                                                />
                                            )
                                        }
                                    })}
                            </div>
                            {Object.values(this.props.timekeepingStatuses.objects).length > 4 && (
                                <div
                                    style={{
                                        color: "#DDD",
                                        marginTop: -6,
                                        marginLeft: -1,
                                    }}
                                >
                                    &#8230;
                                </div>
                            )}
                        </div>
                    ) : null}
                </div>
            )
        }
    }
}

const mapStateToProps = state => {
    const workRuleSets = Object.values(state.entities.workRuleSets.objects)
    return {
        timekeepingStatuses: state.entities.timekeepingStatuses,
        exceptions: state.exceptions,
        workRuleSets,
        ptoRecordsByEmployeeDate: selectPTORecordsByEmployeeDate(state),
        ptoFulfillment: selectPTOFulfillment(state),
    }
}
export default connect(mapStateToProps)(TimecardCellRenderer)
